import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import styles from './DataSinksTable.module.scss';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { Tooltip } from '@mui/material';
import { selectCard } from 'js/actions';
import DataSinkDetailActionsContainer from 'js/components/data_sinks/cards/DataSinkDetailActionsContainer';
import { DataSinkDetailHeader } from 'js/components/data_sinks/cards/DataSinkDetailHeader';
import TagsView from 'js/components/elements/tags/TagsView';
import { RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';
import { SearchFilterViewsType } from 'js/constants/visibilityFilters';
import { isOwner, ownershipLabel, shouldDisplayOwnershipLabel } from 'js/lib/ACLUtils';
import { useListOldReduxContainer } from 'new/elements/DataCard/useListOldReduxContainer';
import { mapStateToProps } from 'js/components/data_sinks/cards/stateMapper';
import DateUtils from 'js/lib/DateUtils';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import DataCard, { DataCardCell, NameDescCell } from 'new/elements/DataCard';
import DataCardList from 'new/elements/DataCardList/DataCardList';
import { AvailableFilters } from 'js/components/MacroInput/types/AvailableFilters';
import Typography from 'new/elements/Typography';
import { Stack } from 'new/elements/Stack';
import FlowTypeIcon from 'js/components/pipelines/cards/FlowTypeIcon';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { DetailsIconButton } from 'new/elements/Button';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { useResourceHeaderTabs } from 'new/hooks/useResourceHeaderTabs';

type Props = {
  urlId?: string;
  data: any[];
  isLoading: boolean;
  isSelection: boolean;
  onSelectionChange: (selection: any[]) => any;
  availableFilterTypes: string[];
  getSourceMeta: (id: number) => any;
  loadData: (page: number, sortCriteria: string, availableFilters: AvailableFilters) => void;
  view: SearchFilterViewsType;
  filters: any;
  noContentView: ReactNode;
};

const makeRowRenderer =
  ({ handleCardSelected, getData, aclQueryMode, onSelectItem, isSelection, selectedItems }) =>
  (index, item) => {
    const {
      createdAt,
      owner,
      cardData,
      readOnly,
      isManaged,
      sinkName,
      sinkIcon,
      writingDataSetContent,
    } = getData(index);
    const readOnlyLabel = shouldDisplayOwnershipLabel(aclQueryMode)
      ? ownershipLabel(cardData)
      : null;

    const onClick = (event: any) => {
      event?.stopPropagation();
      if (isSelection) {
        onSelectItem(cardData);
      } else {
        handleCardSelected(cardData);
      }
    };

    return (
      <DataCard
        key={item.id || index}
        containerClassName={
          isSelection && selectedItems.some((s) => s.id === item.id)
            ? styles.selectedItemContainer
            : null
        }
        className={styles.cardContainer}
        data-testid={`sink-${item.id}`}
        onClick={onClick}
        rowsSlots={[
          <>
            <NameDescCell
              name={cardData.name}
              className={styles.nameCardCell}
              tip={writingDataSetContent}
            />
            ,
            <DataCardCell rightCTAsCell className={styles.sinkCardCell}>
              <PipelineStatusBadge
                id={cardData.id}
                revertDirection
                status={cardData.status as Status}
                runtimeStatus={cardData?.runtime_status}
                ingestionMode={cardData.ingestion_mode}
                parentArchivalStatus={cardData?.archival_status}
              />
              <DetailsIconButton onClick={onClick} />
              {!isSelection ? (
                <DataSinkDetailActionsContainer
                  data={cardData}
                  readOnly={readOnly}
                  isManaged={isManaged}
                  openDetailsModal={() => handleCardSelected(cardData)}
                />
              ) : null}
            </DataCardCell>
          </>,
          <DataCardCell className={styles.sinkCardCell}>
            <Stack gap="16px" direction="row" alignItems="center" justifyContent="flex-end">
              <Tooltip title={cardData.name} arrow>
                <Stack className={styles.resourceInfo}>
                  <ConnectionTypeChip
                    icon={sinkIcon}
                    label={sinkName}
                    style={{ border: 'none', padding: 0 }}
                  />
                </Stack>
              </Tooltip>
              <ChipWithCopy tooltipTitle="Copy Destination ID" toCopy={cardData.id}>
                ID {cardData.id}
              </ChipWithCopy>
              <FlowTypeIcon data={cardData} className={styles.flowTypeContainer} />
            </Stack>
          </DataCardCell>,
          <DataCardCell className={styles.sinkCardCell}>
            <div>
              <Typography variant="body3">Created on {createdAt}</Typography>
              {readOnlyLabel && (
                <div>
                  <Typography variant="caption">{readOnlyLabel}</Typography>
                  {!isOwner(cardData) && <Typography variant="body3"> • Owned by</Typography>}
                  {!isOwner(cardData) && (
                    <Typography variant="body3">{owner && owner.email}</Typography>
                  )}
                </div>
              )}
            </div>
          </DataCardCell>,
          !isEmpty(cardData.tags) && (
            <DataCardCell className={styles.sinkCardCell}>
              <TagsView data={cardData} resourceType={RESOURCE_TYPE_SINK} />
            </DataCardCell>
          ),
        ].filter(Boolean)}
      />
    );
  };

const DataSinksTable = ({
  availableFilterTypes,
  view,
  urlId,
  isSelection,
  onSelectionChange,
  isLoading,
  data,
  getSourceMeta,
  loadData,
  noContentView,
}: Props) => {
  const dispatch = useAppDispatch();
  const [selectedItems, setSelectedItems] = useState([]);
  const listing = useSelector((state: any) => state.app.listing);
  const { selectedCardId, aclQueryMode } = listing;

  const selectedSink = data?.find((sink) => sink.id === selectedCardId);
  const detailViewTabs = (useResourceHeaderTabs(selectedSink, {
    resourceType: RESOURCE_TYPE_SINK,
    flow: null,
  }) || []) as any[];

  const handleCardSelected = useCallback((row) => {
    dispatch(selectCard(row.id));
  }, []);

  useEffect(() => {
    if (urlId) {
      handleCardSelected({ id: urlId });
    }
  }, []);

  const getData = useListOldReduxContainer(data, mapStateToProps, (ownProps) => {
    const {
      cardData,
      readOnly,
      adminAccess,
      aclQueryMode,
      isManaged,
      owner,
      writingDataSet,
      isWritingDataset,
      connectionOrVendorType,
    } = ownProps;
    const createdAt = DateUtils.convertDateStringToFormat(cardData?.created_at);

    const description = cardData?.description || ' ';
    const connectionInfo = getSourceMeta(cardData?.id);

    let sinkIcon;
    let sinkName;

    if (connectionInfo) {
      sinkIcon = connectionInfo.iconSm;
      sinkName = connectionInfo.name;
    } else {
      sinkName = connectionOrVendorType;
    }
    const writingDataSetName = isWritingDataset && writingDataSet ? writingDataSet.name : null;

    let writingDataSetContent = null;
    if (isWritingDataset === true) {
      writingDataSetContent = writingDataSetName ? (
        <div className="secondary-label" title={writingDataSetName}>
          <b>WRITING NEXSET: </b>
          {writingDataSetName}
        </div>
      ) : (
        <div className="secondary-label" title={cardData?.data_set_id}>
          <b>WRITING NEXSET ID: </b>
          {cardData?.data_set_id}
        </div>
      );
    } else {
      writingDataSetContent = (
        <div className="secondary-label" title={'no-dataset'}>
          <b>WRITING NEXSET : </b>
          Nexset not configured
        </div>
      );
    }

    return {
      description,
      createdAt,
      owner,
      cardData,
      readOnly,
      adminAccess,
      aclQueryMode,
      isManaged,
      sinkName,
      sinkIcon,
      writingDataSetContent,
    };
  });

  const onSelectItem = (data) => {
    if (selectedItems.includes(data)) {
      selectedItems.splice(selectedItems.indexOf(data), 1);
    } else {
      selectedItems.push(data);
    }
    const newItems = [...selectedItems];
    setSelectedItems(newItems);
    if (onSelectionChange) {
      onSelectionChange(newItems);
    }
  };

  return (
    <DataCardList
      data={data}
      view={view}
      availableFilterTypes={availableFilterTypes}
      detailViewTabs={detailViewTabs}
      urlId={urlId}
      loading={isLoading}
      detailHeaderContent={
        <DataSinkDetailHeader
          {...getData(data.findIndex((item: any) => item.id === selectedCardId))}
        />
      }
      rowRenderer={makeRowRenderer({
        selectedItems,
        handleCardSelected,
        onSelectItem,
        getData,
        isSelection,
        aclQueryMode,
      })}
      getSourceMeta={getSourceMeta}
      noContentView={noContentView}
      loadData={loadData}
    />
  );
};

export default DataSinksTable;
